/**
 * Sticky Info section
 * - Desktop: sticky nav, smooth scroll, scroll-spy active state
 * - Mobile: dropdown opens popup grid; select scrolls + closes
 * - Mobile toggle label always reflects the active/selected item
 */
if (!customElements.get('sticky-info')) {
  customElements.define(
    'sticky-info',
    class StickyInfo extends HTMLElement {
      constructor() {
        super();
        this.lockActiveUntil = 0;
        this.rafId = null;
        this.onDocClick = this.onDocClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onNavClick = this.onNavClick.bind(this);
        this.onToggleClick = this.onToggleClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        this.navLinks = this.querySelectorAll('[data-sticky-info-link]');
        this.panels = this.querySelectorAll('[data-sticky-info-panel]');
        this.toggle = this.querySelector('[data-sticky-info-toggle]');
        this.popup = this.querySelector('[data-sticky-info-popup]');
        this.toggleLabel = this.querySelector('[data-sticky-info-toggle-label]');

        for (const link of this.navLinks) {
          link.addEventListener('click', this.onNavClick);
        }

        this.toggle?.addEventListener('click', this.onToggleClick);
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.syncActiveFromScroll();
      }

      disconnectedCallback() {
        for (const link of this.navLinks) {
          link.removeEventListener('click', this.onNavClick);
        }

        this.toggle?.removeEventListener('click', this.onToggleClick);
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('scroll', this.onScroll);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      get stickyOffset() {
        const raw = this.dataset.stickyOffset;
        const parsed = Number.parseInt(raw || '0', 10);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      onScroll() {
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.syncActiveFromScroll();
        });
      }

      /**
       * Active section = last panel whose top is at or above the reading line.
       * Works reliably when scrolling up or down.
       */
      getActivePanelId() {
        if (!this.panels.length) return null;

        const readingLine = this.stickyOffset + 24;
        let activeId = this.panels[0].getAttribute('id');

        for (const panel of this.panels) {
          const top = panel.getBoundingClientRect().top;
          if (top <= readingLine) {
            activeId = panel.getAttribute('id');
          } else {
            break;
          }
        }

        return activeId;
      }

      syncActiveFromScroll() {
        if (Date.now() < this.lockActiveUntil) return;

        const id = this.getActivePanelId();
        if (id) this.setActive(id);
      }

      setActive(panelId) {
        let labelText = '';

        for (const link of this.navLinks) {
          const isActive = link.getAttribute('href') === `#${panelId}`;
          link.classList.toggle('is-active', isActive);

          if (isActive) {
            link.setAttribute('aria-current', 'true');
            labelText = link.textContent.trim();
          } else {
            link.removeAttribute('aria-current');
          }
        }

        if (this.toggleLabel && labelText) {
          this.toggleLabel.textContent = labelText;
        }
      }

      onNavClick(event) {
        const link = event.currentTarget;
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        const target = this.querySelector(href);
        if (!target) return;

        event.preventDefault();

        const panelId = href.slice(1);
        // Keep the clicked item as selected while smooth-scroll finishes
        this.lockActiveUntil = Date.now() + 1000;
        this.setActive(panelId);
        this.closePopup();

        const top = target.getBoundingClientRect().top + window.scrollY - this.stickyOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      }

      onToggleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.popup?.hidden) {
          this.openPopup();
        } else {
          this.closePopup();
        }
      }

      openPopup() {
        if (!this.popup || !this.toggle) return;
        this.popup.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        this.classList.add('is-popup-open');
      }

      closePopup() {
        if (!this.popup || !this.toggle) return;
        this.popup.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-popup-open');
      }

      onDocClick(event) {
        if (!this.classList.contains('is-popup-open')) return;
        if (this.contains(event.target)) return;
        this.closePopup();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.closePopup();
        }
      }
    }
  );
}
